import type { ColumnDef } from '@tanstack/react-table';
import { Archive, BadgeCheck, GitMerge } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import type { LeoEntry } from '../types';

interface LeoColumnsProps {
  onEdit?: ((entry: LeoEntry) => void) | undefined;
  onVerify?: ((entry: LeoEntry) => void) | undefined;
  onMerge?: ((entry: LeoEntry) => void) | undefined;
  onArchive?: ((entry: LeoEntry) => void) | undefined;
  hasAccess?: ((resource: string, action: string) => boolean) | undefined;
  isVerifying?: boolean | undefined;
  isArchiving?: boolean | undefined;
}

// Case 360° (Stage 8) — LEO directory table columns (same hook shape as
// useMatterColumns / useClientColumns).
export function useLeoColumns({
  onEdit,
  onVerify,
  onMerge,
  onArchive,
  hasAccess = () => true,
  isVerifying,
  isArchiving,
}: LeoColumnsProps = {}): ColumnDef<LeoEntry>[] {
  const { t } = useTranslation();

  const canUpdate = hasAccess('leo', 'update');
  const canCurate = hasAccess('leo', 'verify');
  const canArchive = hasAccess('leo', 'delete');

  const columns: ColumnDef<LeoEntry>[] = [
    {
      accessorKey: 'name',
      header: () => <p>{t('leo.nameLabel')}</p>,
      cell: ({ row }) => {
        const entry = row.original;
        return (
          <div>
            <span className="font-medium">{entry?.name}</span>
            {entry?.deletedAt && (
              <Badge variant="outline" className="ml-2 text-xs">
                {t('leo.archived')}
              </Badge>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: 'type',
      header: () => <p>{t('leo.typeLabel')}</p>,
      cell: ({ row }) => <p>{t(`leo.type.${row.original?.type}`)}</p>,
    },
    {
      accessorKey: 'city',
      header: () => <p>{t('leo.cityLabel')}</p>,
      cell: ({ row }) => <p>{row.original?.city}</p>,
    },
    {
      accessorKey: 'phone',
      header: () => <p>{t('leo.phoneLabel')}</p>,
      cell: ({ row }) => <p>{row.original?.phone ?? '—'}</p>,
    },
    {
      accessorKey: 'status',
      header: () => <p>{t('leo.statusLabel')}</p>,
      cell: ({ row }) => (
        <Badge
          className={
            row.original?.status === 'VERIFIED'
              ? 'border border-green-200 bg-green-100 text-green-700'
              : 'border border-amber-200 bg-amber-50 text-amber-700'
          }
        >
          {t(`leo.status.${row.original?.status}`)}
        </Badge>
      ),
    },
  ];

  // Add actions column if user has any management permission
  if (canUpdate || canCurate || canArchive) {
    columns.push({
      id: 'actions',
      header: () => <p className="text-right">{t('actions.actions')}</p>,
      cell: ({ row }) => {
        const entry = row.original;
        if (entry?.deletedAt) return null;
        return (
          <div className="flex justify-end gap-1.5">
            {canCurate && entry?.status === 'DRAFT' && (
              <Button
                size="sm"
                variant="outline"
                className="border-green-300 text-green-700 hover:bg-green-50"
                onClick={() => onVerify?.(entry)}
                disabled={isVerifying}
                title={t('leo.verify')}
              >
                <BadgeCheck className="h-4 w-4" />
              </Button>
            )}
            {canUpdate && (
              <Button size="sm" variant="outline" onClick={() => onEdit?.(entry)}>
                {t('edit')}
              </Button>
            )}
            {canCurate && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => onMerge?.(entry)}
                title={t('leo.merge')}
              >
                <GitMerge className="h-4 w-4" />
              </Button>
            )}
            {canArchive && (
              <Button
                size="sm"
                variant="outline"
                className="border-red-200 text-red-600 hover:bg-red-50"
                onClick={() => onArchive?.(entry)}
                disabled={isArchiving}
                title={t('leo.archive')}
              >
                <Archive className="h-4 w-4" />
              </Button>
            )}
          </div>
        );
      },
    });
  }

  return columns;
}
